import { Popover } from 'antd';
import { useGetAPIProductDetails, useGetEnvironmentUsagePlans, useListAPIProductPlanDetails } from 'api/hooks';
import { webAppAPI } from 'api/web-app';
import { ReactComponent as CalendarIcon } from 'assets/calendar-icon.svg';
import { ReactComponent as CodeGearIcon } from 'assets/code-sprocket-icon.svg';
import { ReactComponent as EnvironmentIcon } from 'assets/environment-icon.svg';
import { ReactComponent as KeyIcon } from 'assets/group-policy-icon.svg';
import { ReactComponent as NoApiKeyIcon } from 'assets/no-api-key-icon.svg';
import { ReactComponent as PlanIcon } from 'assets/plan-icon.svg';
import { ReactComponent as DeleteIconX } from 'assets/small-grey-x.svg';
import { ReactComponent as EyeIcon } from 'assets/view-icon.svg';
import { copyTextToClipboard, formatDate, formatUnit, sortByName } from 'helper-utils';
import { ObjectRef } from 'proto/github.com/solo-io/dev-portal/api/dev-portal/v1beta1/common/common_pb';
import { PlanKey, PlanSummary } from 'proto/web_app_grpc_pb';
import { WebAppApi } from 'proto/web_app_grpc_pb_service';
import * as React from 'react';
import { trigger } from 'swr';
import { Card } from './Card';
import { ConfirmationModal } from './ConfirmationModal';
import { CreateAPIKeyModal } from './CreateAPIKeyModal';
import './KeyCard.css';

interface APIProductCardProps {
  apiProductName: string;
  apiProductNamespace: string;
  displayName?: string;
  description?: string;
  onClick?: () => any;
}

export const APIProductCard = ({
  displayName,
  description,
  apiProductName,
  apiProductNamespace
}: APIProductCardProps) => {
  const [addKeyModalOpen, setAddKeyModalOpen] = React.useState(false);
  const [confirmingKeyDeletionRef, setConfirmingKeyDeletionRef] = React.useState<ObjectRef.AsObject | null>(null);
  const [keysBeingShown, setKeysBeingShown] = React.useState<{ [key: string]: boolean }>({});
  const [attemptedCopy, setAttemptedCopy] = React.useState<string | null>(null);
  const [usagePlanToAdd, setUsagePlanToAdd] = React.useState('');
  const [environmentForAPIKey, setEnvironmentForAPIKey] = React.useState<ObjectRef.AsObject>();
  const [keyCount, setKeyCount] = React.useState(0);
  const [planCount, setPlanCount] = React.useState(0);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { apiProductPlans, apiProductPlansLoading, apiProductPlansError } = useListAPIProductPlanDetails();

  const { apiProductDetails, apiProductDetailsLoading, apiProductDetailsError } = useGetAPIProductDetails({
    name: apiProductName,
    namespace: apiProductNamespace
  });

  const {
    environmentUsagePlans,
    environmentUsagePlansError,
    environmentUsagePlansMutate
  } = useGetEnvironmentUsagePlans({
    name: apiProductName,
    namespace: apiProductNamespace
  });

  React.useEffect(() => {
    if (!!apiProductPlans) {
      let keys = 0;
      let plans = 0;
      apiProductPlans?.forEach(plan => {
        if (apiProductName === plan.metadata?.name && apiProductNamespace === plan?.metadata?.namespace) {
          keys += plan.apiKeyCount;
          plans += plan.planCount;
        }
      });
      setKeyCount(keys);
      setPlanCount(plans);
    }
  }, [apiProductPlansLoading, apiProductDetailsLoading, addKeyModalOpen]);

  const copyKey = (key: string) => {
    setAttemptedCopy(copyTextToClipboard(key) ? key : '');

    setTimeout(() => {
      setAttemptedCopy(null);
    }, 1000);
  };

  const openAddKeyModal = (envRef: ObjectRef.AsObject, usagePlan: string) => {
    setAddKeyModalOpen(true);
    setUsagePlanToAdd(usagePlan);
    setEnvironmentForAPIKey(envRef);
  };
  const cancelAddition = () => {
    setAddKeyModalOpen(false);
  };

  const generateKey = (apiProductId: string, metadata: [string, string][]) => {
    if (!apiProductId) {
      return;
    }
    webAppAPI
      .addAPIKey({
        apiProductRef: { name: apiProductName, namespace: apiProductNamespace },
        environmentRef: environmentForAPIKey,
        usagePlanName: usagePlanToAdd,
        customStringDataMap: metadata
      })
      .then(() => {
        // Wait a bit before reloading api keys to give time for writes to propagate
        setTimeout(() => {
          trigger(`${WebAppApi.ListAPIProductPlanDetails.methodName}/${apiProductId}`);
        }, 1000);
        environmentUsagePlansMutate();
      })
      .catch(err => console.error(err));

    setAddKeyModalOpen(false);
    setUsagePlanToAdd('');
    setEnvironmentForAPIKey(undefined);
  };

  const toggleKeyShowing = (evt: React.MouseEvent, keyInfo: PlanKey.AsObject) => {
    evt.stopPropagation();
    setKeysBeingShown({ ...keysBeingShown, ...{ [keyInfo.key]: !keysBeingShown[keyInfo.key] } });
  };

  const attemptDeleteKey = (evt: React.MouseEvent, ref: ObjectRef.AsObject) => {
    evt.stopPropagation();

    setConfirmingKeyDeletionRef(ref);
  };

  const deleteKey = (ref: ObjectRef.AsObject | null) => {
    if (!ref) {
      return;
    }
    webAppAPI
      .deleteAPIKey(ref)
      .then(() => {
        // Wait a bit before reloading api keys to give time for writes to propagate
        setTimeout(() => {
          trigger(`${WebAppApi.ListAPIProductPlanDetails.methodName}/${ref.namespace}/${ref.name}`);
          environmentUsagePlansMutate();
        }, 500);
      })
      .catch(err => console.error(err));

    setConfirmingKeyDeletionRef(null);
  };

  const cancelDeletion = () => {
    setConfirmingKeyDeletionRef(null);
  };

  const handleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      <div className='key-card-container'>
        <Card>
          <>
            <div className={`key-card-core-info ${isExpanded ? 'expanded' : ''}`} onClick={handleExpand}>
              <div className='key-card-app-title-container'>
                <div className=' key-card-apiproduct-icon'>
                  <CodeGearIcon className='w-16 h-16 fill-current' title='' />
                </div>
                <div className='key-card-app-apiproduct-container'>
                  <div className='key-card-app-title'>{apiProductDetails?.displayname}</div>
                  <div className='key-card-app-description'>{apiProductDetails?.description || ''}</div>
                </div>
              </div>
              <div className='key-card-divider' />
              <div className='key-card-icons-container '>
                <div className='key-card-count-container '>
                  <div className='key-card-count-circle'>
                    <KeyIcon title='' />
                  </div>
                  <span className='count-numeral'>{keyCount}</span> API Keys
                </div>

                <div className='key-card-count-container '>
                  <div className='key-card-count-circle'>
                    <PlanIcon title='' />
                  </div>
                  <span className='count-numeral'>{planCount}</span> Plans
                </div>
              </div>
            </div>
            <div className={`key-card-extra-info ${isExpanded ? 'expanded' : ''}`}>
              <>
                {environmentUsagePlans?.map(env => (
                  <React.Fragment key={env.metadata?.uid}>
                    <div className='key-card-keys-list-title'>
                      <span className='key-card-icon-container'>
                        <EnvironmentIcon title='' />
                      </span>
                      {env.displayName}
                    </div>
                    <div className='mx-4 bg-white border rounded-lg '>
                      {env.plansList?.length === 0 && (
                        <div className='key-card-access-list'>
                          <div className='key-card-access-list-title'> Plans</div>
                          <div className='key-card-nothing-found-info'>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              height='30px'
                              width='30px'
                              viewBox='0 0 22.001 22'
                              aria-hidden='true'>
                              <path
                                fill='#2196c9'
                                d='M18.77 4.701A11 11 0 103.233 20.27a.588.588 0 00.788-.006l1.832-1.834a.553.553 0 000-.781.565.565 0 00-.781 0l-1.448 1.448-.131-.152a9.974 9.974 0 01-2.37-5.73l-.015-.2h2.041a.533.533 0 000-1.066H1.102l.016-.2a9.979 9.979 0 012.365-5.734l.131-.154 1.422 1.423a.542.542 0 00.753-.779L4.364 5.082l.152-.131A9.775 9.775 0 0110.25 2.59l.2-.013v2.038a.532.532 0 101.065 0V2.574l.2.016a9.988 9.988 0 015.734 2.364l.154.131-1.45 1.448a.542.542 0 10.753.779l1.445-1.445.131.151a9.781 9.781 0 012.365 5.737l.013.2h-1.99a.53.53 0 00-.373.154.522.522 0 00-.157.375.557.557 0 00.539.564h2.038l-.015.2a9.908 9.908 0 01-2.364 5.733l-.131.154-1.45-1.448a.552.552 0 00-.781.781l1.84 1.84a.54.54 0 00.748 0 11.031 11.031 0 00.006-15.6z'
                              />
                              <path
                                fill='#2196c9'
                                d='M10.985 10.425a1.876 1.876 0 00-.881.216l-.119.063-2.138-2.112a.543.543 0 00-.769.767l2.14 2.138-.065.12a1.843 1.843 0 00-.214.881 2.041 2.041 0 002.042 2.046 2.043 2.043 0 002.043-2.042 2.071 2.071 0 00-2.039-2.077zm.186 3.017v.021h-.183a.969.969 0 11.969-.969.989.989 0 01-.786.948z'
                              />
                            </svg>
                            <div className='key-card-no-api-key-text'>No Plans have been set.</div>
                          </div>
                        </div>
                      )}
                      {env.plansList
                        ?.filter(usagePlan => usagePlan.authPolicyType === PlanSummary.AuthPolicyType.API_KEY)
                        ?.sort(sortByName)
                        .map(usagePlan => (
                          <div key={usagePlan.name} style={{ position: 'relative' }}>
                            <button
                              className='key-card-add-key-container'
                              onClick={() =>
                                openAddKeyModal(
                                  { name: env.metadata?.name!, namespace: env?.metadata?.namespace! },
                                  usagePlan.name
                                )
                              }>
                              <div className='key-card-plus-circle'>
                                <div className='key-card-plus-sign'>+</div>
                              </div>
                              Add API Key
                            </button>
                            <div className='key-card-keys-list '>
                              <>
                                <div className='key-card-keys-list-title'>
                                  {usagePlan.displayName}{' '}
                                  <span className='key-card-usage-plan'>
                                    {formatUnit(usagePlan.unit) !== ''
                                      ? `(${usagePlan.requestsPerUnit} per ${formatUnit(usagePlan.unit)})`
                                      : ''}
                                  </span>
                                </div>
                                {usagePlan.apiKeysList.length === 0 ? (
                                  <div className='key-card-nothing-found-info'>
                                    <span className=' text-blue-500-gloo'>
                                      <NoApiKeyIcon className='w-16 h-16 fill-current' title='' />
                                    </span>
                                    <div className='key-card-no-api-key-text'>
                                      There are no API Keys to display.
                                      <div className='key-card-sub-title '>
                                        Please generate a key to access this API Product using this usage plan.
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  usagePlan.apiKeysList
                                    .sort((a, b) => (a.key === b.key ? 0 : a.key > b.key ? 1 : -1))
                                    .map(apiKey => (
                                      <React.Fragment key={apiKey.key}>
                                        <button
                                          className={`key-card-api-key-container ${
                                            attemptedCopy !== '' && attemptedCopy === apiKey.key
                                              ? 'copy-successful'
                                              : ''
                                          }`}
                                          onClick={() => copyKey(apiKey.key)}>
                                          <div className='key-card-icon-container'>
                                            <KeyIcon title='' />
                                          </div>
                                          <Popover
                                            key='#000000'
                                            color='#000000'
                                            content={
                                              attemptedCopy !== '' && attemptedCopy === apiKey.key ? (
                                                <div style={{ display: 'grid', placeContent: 'center' }}>Copied!</div>
                                              ) : (
                                                <div style={{ display: 'grid', placeContent: 'center' }}>
                                                  Click to copy
                                                </div>
                                              )
                                            }>
                                            <div className='key-card-key-text '>
                                              {keysBeingShown[apiKey.key]
                                                ? apiKey.key
                                                : '******************************************************************************'}
                                            </div>
                                          </Popover>
                                          <button
                                            aria-label={`${keysBeingShown[apiKey.key] ? 'Hide' : 'Show'} key`}
                                            className={`key-card-toggle ${
                                              keysBeingShown[apiKey.key] ? 'is-shown' : ''
                                            }`}
                                            onClick={evt => toggleKeyShowing(evt, apiKey)}>
                                            <EyeIcon title='' />
                                            {keysBeingShown[apiKey.key] ? 'hide' : 'show'}
                                          </button>
                                          <div className='key-card-key-created-on'>
                                            <div className='key-card-calendar-icon-container '>
                                              <CalendarIcon fill='#2196C9' title='' />
                                            </div>
                                            {formatDate(apiKey.metadata?.creationTimestamp!)}
                                          </div>
                                          <div className='key-card-delete-container'>
                                            <button
                                              aria-label='Delete'
                                              className='key-card-delete-circle'
                                              onClick={evt => {
                                                attemptDeleteKey(evt, {
                                                  name:
                                                    (apiKey.metadata?.name ?? '') === ''
                                                      ? apiKey.key
                                                      : apiKey.metadata?.name ?? '',
                                                  namespace: apiKey.metadata?.namespace ?? ''
                                                });
                                              }}>
                                              <DeleteIconX title='' />
                                            </button>
                                          </div>
                                        </button>
                                      </React.Fragment>
                                    ))
                                )}
                              </>
                            </div>
                          </div>
                        ))}
                    </div>
                  </React.Fragment>
                ))}
              </>
            </div>
          </>
        </Card>
      </div>

      <CreateAPIKeyModal
        visible={addKeyModalOpen}
        goForIt={(metadata: [string, string][]) => generateKey(`${apiProductNamespace}_${apiProductName}`, metadata)}
        cancel={cancelAddition}
      />
      <ConfirmationModal
        visible={confirmingKeyDeletionRef !== null}
        confirmationTopic='delete this key'
        confirmText='Delete'
        goForIt={() => deleteKey(confirmingKeyDeletionRef)}
        cancel={cancelDeletion}
        isNegative={true}
      />
    </>
  );
};
